// Placeable lifecycle and trigger pipeline. ADR 0009 §2.9.
//
// Trigger kinds (PlaceableTrigger.kind):
//   tick-pulse / aura-zone / contact ........ implemented in PR B-1
//   projectile-shield / manual ............... explicit stubs (separate PR)
//
// Target side rules for `firePlaceableEffects` are pinned by
// `pickPlaceableTargets` so onTriggerEffects can rely on a stable contract:
//   damage / applyDebuff       → enemy side
//   knockback / pull           → enemy side (P55-024: displace surrounding foes)
//   heal / applyBuff / shield  → ally side (relative to placeable owner)
//   cleanse                    → ally side
//   everything else            → owner only
import { applyEffect } from '../effects.js';
import { fireReactiveSkills } from './reactive-events.js';
function distance(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return Math.sqrt(dx * dx + dy * dy);
}
/** Allocate the next free runtime EntityId given the current state. Used for
 *  placeables and summons that do not come from the static hero / enemy
 *  catalogue. */
export function nextRuntimeId(state) {
    let max = 0;
    for (const e of state.entities)
        if (e.id > max)
            max = e.id;
    for (const p of state.placeables)
        if (p.id > max)
            max = p.id;
    return max + 1;
}
/** Resolve the spawn position for a `placePlaceable` spec. ADR 0009 §2.9. */
export function resolvePlacement(spec, caster, primaryTarget, state) {
    switch (spec.placementTarget ?? 'caster') {
        case 'caster':
            return { x: caster.pos.x, y: caster.pos.y };
        case 'enemy-feet': {
            if (primaryTarget !== null && primaryTarget.hp > 0) {
                return { x: primaryTarget.pos.x, y: primaryTarget.pos.y };
            }
            let nearest = null;
            let bestDist = Infinity;
            for (const e of state.entities) {
                if (e.side === caster.side || e.hp <= 0)
                    continue;
                const d = distance(caster.pos, e.pos);
                if (d < bestDist) {
                    bestDist = d;
                    nearest = e;
                }
            }
            return nearest !== null ? { x: nearest.pos.x, y: nearest.pos.y } : null;
        }
        case 'lowest-hp-ally': {
            let lowest = null;
            let bestPct = Infinity;
            for (const e of state.entities) {
                if (e.side !== caster.side || e.hp <= 0)
                    continue;
                const pct = e.hp / e.hpMax;
                if (pct < bestPct) {
                    bestPct = pct;
                    lowest = e;
                }
            }
            return lowest !== null ? { x: lowest.pos.x, y: lowest.pos.y } : null;
        }
    }
}
/**
 * Spawn a Placeable from a PlaceableSpec. Applies the per-owner concurrency
 * cap by despawning the oldest of this template+owner pair when the cap is
 * reached. Returns null when placement cannot be resolved (e.g. enemy-feet
 * with no enemies on the field).
 */
export function spawnPlaceable(spec, caster, primaryTarget, state) {
    const position = resolvePlacement(spec, caster, primaryTarget, state);
    if (position === null)
        return null;
    // FIFO cap: drop the oldest matching placeable until we have room.
    while (true) {
        const owned = state.placeables.filter((p) => p.ownerId === caster.id && p.templateId === spec.templateId);
        if (owned.length < spec.maxConcurrentPerOwner)
            break;
        const oldest = owned[0];
        const idx = state.placeables.indexOf(oldest);
        if (idx >= 0)
            state.placeables.splice(idx, 1);
    }
    const placeable = {
        id: nextRuntimeId(state),
        side: caster.side,
        ownerId: caster.id,
        templateId: spec.templateId,
        position,
        radius: spec.radius,
        durationTicks: spec.durationTicks,
        expiresAt: state.tick + spec.durationTicks,
        isWalkable: spec.isWalkable,
        trigger: spec.trigger,
        onTriggerEffects: spec.onTriggerEffects,
    };
    if (spec.trigger.kind === 'tick-pulse') {
        placeable.nextPulseAt = state.tick + spec.trigger.periodTicks;
    }
    if (spec.trigger.kind === 'aura-zone') {
        // First pulse on the first lifecycle pass after placement (placement
        // happens in the combat step, after tickPlaceables, so this is tick+1) —
        // entering a zone still bites promptly; see AURA_ZONE_PERIOD_TICKS.
        placeable.nextPulseAt = state.tick;
    }
    if (spec.hp !== undefined)
        placeable.hp = spec.hp;
    state.placeables.push(placeable);
    return placeable;
}
/**
 * Aura zones pulse once per second (W1-2, ADR 0042). The previous every-tick
 * firing multiplied any damage/heal/debuff aura by the 30 Hz tick rate —
 * e.g. `ult.paladin.holy-aegis` (damage power 1.4) dealt ~42 × weaponBase
 * per SECOND and one-shot recommended-level melee heroes at the T3 boss,
 * and the same zone made the T4 paladin hero trivialize its tier. The
 * authored aura powers (0.05-1.4) and the sibling tick-pulse archetype
 * (periodTicks 30) are all sized for ~1 Hz. Buff-refresh auras stay
 * continuous because their buff durations (60+) exceed the period.
 */
const AURA_ZONE_PERIOD_TICKS = 30;
/** Pop expired or destroyed placeables and fire trigger payloads. ADR 0009 §2.9. */
export function tickPlaceables(state) {
    if (state.placeables.length === 0)
        return;
    const tick = state.tick;
    const remaining = [];
    for (const p of state.placeables) {
        if (p.expiresAt <= tick || (p.hp !== undefined && p.hp <= 0)) {
            // ADR 0014 §2.4: 消滅 (期限切れ / 破壊統合) で onPlaceableEnd 発火。
            fireReactiveSkills(state, p.ownerId, 'onPlaceableEnd', null);
            continue;
        }
        firePlaceableTrigger(p, state);
        // contact triggers may have set expiresAt = tick to mark consumption; honor it.
        if (p.expiresAt <= tick) {
            fireReactiveSkills(state, p.ownerId, 'onPlaceableEnd', null);
            continue;
        }
        remaining.push(p);
    }
    state.placeables = remaining;
}
function firePlaceableTrigger(p, state) {
    const tick = state.tick;
    switch (p.trigger.kind) {
        case 'tick-pulse': {
            if (p.nextPulseAt !== undefined && p.nextPulseAt <= tick) {
                firePlaceableEffects(p, state);
                p.nextPulseAt = tick + p.trigger.periodTicks;
            }
            return;
        }
        case 'aura-zone': {
            if (p.nextPulseAt === undefined || p.nextPulseAt <= tick) {
                firePlaceableEffects(p, state);
                p.nextPulseAt = tick + AURA_ZONE_PERIOD_TICKS;
            }
            return;
        }
        case 'contact': {
            const owner = state.entities.find((e) => e.id === p.ownerId);
            if (owner === undefined)
                return;
            let triggered = false;
            for (const e of state.entities) {
                if (e.side === owner.side || e.hp <= 0)
                    continue;
                if (distance(e.pos, p.position) <= p.radius) {
                    triggered = true;
                    break;
                }
            }
            if (triggered) {
                firePlaceableEffects(p, state);
                if (p.trigger.consumesOnTrigger)
                    p.expiresAt = tick;
            }
            return;
        }
        case 'manual':
            // Manual placeables are fired by triggerManualPlaceables when their
            // linked skill is actually cast. They do not auto-pulse from lifecycle.
            return;
        case 'projectile-shield':
            // Projectile shields are consumed as pre-hit miss chance in damage paths.
            return;
    }
}
export function projectileShieldMissChanceBonus(target, state) {
    let bonus = 0;
    for (const p of state.placeables) {
        if (p.trigger.kind !== 'projectile-shield')
            continue;
        if (p.side !== target.side)
            continue;
        if (p.hp !== undefined && p.hp <= 0)
            continue;
        if (distance(target.pos, p.position) > p.radius + target.radius)
            continue;
        bonus = Math.max(bonus, p.trigger.missChanceBonus);
    }
    return bonus;
}
export function triggerManualPlaceables(state, owner, linkSkillId) {
    for (const p of state.placeables) {
        if (p.ownerId !== owner.id)
            continue;
        if (p.trigger.kind !== 'manual')
            continue;
        if (p.trigger.linkSkillId !== linkSkillId)
            continue;
        if (p.expiresAt <= state.tick || (p.hp !== undefined && p.hp <= 0))
            continue;
        firePlaceableEffects(p, state);
    }
}
function firePlaceableEffects(p, state) {
    const owner = state.entities.find((e) => e.id === p.ownerId);
    if (owner === undefined)
        return;
    const candidates = [];
    for (const e of state.entities) {
        if (e.hp <= 0)
            continue;
        if (distance(e.pos, p.position) <= p.radius)
            candidates.push(e);
    }
    for (const effect of p.onTriggerEffects) {
        const targets = pickPlaceableTargets(effect, owner, candidates);
        for (const target of targets) {
            applyEffect(effect, owner, target, state, p.templateId, 1 + Math.max(0, owner.placeablePowerBonus ?? 0));
        }
    }
    // ADR 0014 §2.4: trigger 発火後に onPlaceableTrigger reactive を owner 側で発火。
    // 1 tick 1 skill 1 回集約は fireReactiveSkills 側で担保。
    fireReactiveSkills(state, p.ownerId, 'onPlaceableTrigger', null);
}
/**
 * Side rules for placeable-fired effects. ADR 0009 §2.9.
 *
 * Contract: every Effect.kind has a fixed target side here so placeable
 * onTriggerEffects authors don't need to think about side filtering.
 */
export function pickPlaceableTargets(effect, owner, candidates) {
    switch (effect.kind) {
        // damage / applyDebuff target enemies. P55-024: placeable-fired knockback /
        // pull also displace surrounding ENEMIES (beast-tamer warden-howl,
        // magus-knight runic-vortex, dark-lord throne); the resolver moves `target`,
        // so the target must be the enemy, not the owner. dash / consumeNextCast stay
        // owner-targeted below (no production placeable uses them; both act on caster).
        case 'damage':
        case 'applyDebuff':
        case 'knockback':
        case 'pull':
            return candidates.filter((e) => e.side !== owner.side);
        case 'heal':
        case 'applyBuff':
        case 'shield':
        case 'cleanse':
            return candidates.filter((e) => e.side === owner.side);
        case 'summon':
        case 'placePlaceable':
        case 'dash':
        case 'consumeNextCast':
        case 'damageConversion':
        case 'anchoredHealOnHit':
        case 'reflectingProjectile':
        case 'transformOnHit':
        case 'phaseCyclingAura':
        case 'counterStance':
        case 'sharedAbsorbCharge':
        case 'damageStoreAndRelease':
            return [owner];
        // ADR 0013: removeStatusById. PR 1 で sim 側は no-op だが exhaustiveness のため
        // 仮の target side ルールを置く。PR 2 で印消費の実装と合わせて見直す。
        case 'removeStatusById':
            return [owner];
    }
}
//# sourceMappingURL=placeables.js.map